/**
 * EditPaymentAccountModal Component
 * Modal for editing a payment account
 */

import React, { useEffect, useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Textarea from "../../../components/ui/Textarea";
import { Checkbox } from "../../../components/ui/Checkbox";
import type { PaymentAccount } from "../../../store/api/paymentAccountsApi";

interface EditPaymentAccountModalProps {
	account: PaymentAccount;
	isOpen: boolean;
	onClose: () => void;
	onSubmit: (data: {
		account_name?: string;
		account_number?: string;
		bank_name?: string;
		is_active?: boolean;
		notes?: string;
	}) => void;
	isLoading: boolean;
}

const EditPaymentAccountModal: React.FC<EditPaymentAccountModalProps> = ({
	account,
	isOpen,
	onClose,
	onSubmit,
	isLoading,
}) => {
	const [formData, setFormData] = useState({
		account_name: account.account_name,
		account_number: account.account_number,
		bank_name: account.bank_name,
		is_active: account.is_active,
		notes: account.notes || "",
	});

	useEffect(() => {
		if (isOpen) {
			setFormData({
				account_name: account.account_name,
				account_number: account.account_number,
				bank_name: account.bank_name,
				is_active: account.is_active,
				notes: account.notes || "",
			});
		}
	}, [account, isOpen]);

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		onSubmit({
			account_name: formData.account_name,
			account_number: formData.account_number,
			bank_name: formData.bank_name,
			is_active: formData.is_active,
			notes: formData.notes || undefined,
		});
	};

	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
			<div className="bg-card border border-border rounded-lg shadow-xl max-w-md w-full mx-4">
				{/* Header */}
				<div className="px-6 py-4 border-b border-border flex items-center justify-between">
					<h2 className="text-xl font-bold text-foreground">Edit Payment Account</h2>
					<button
						onClick={onClose}
						className="text-muted-foreground hover:text-foreground smooth-transition"
					>
						<Icon name="X" size={24} />
					</button>
				</div>

				{/* Form */}
				<form onSubmit={handleSubmit} className="p-6 space-y-4">
					<Input
						label="Account Name"
						placeholder="e.g., Dofarahub Properties Ltd"
						value={formData.account_name}
						onChange={(e) =>
							setFormData({ ...formData, account_name: e.target.value })
						}
						required
					/>

					<Input
						label="Account Number"
						placeholder="e.g., 1234567890"
						value={formData.account_number}
						onChange={(e) =>
							setFormData({ ...formData, account_number: e.target.value })
						}
						required
					/>

					<Input
						label="Bank Name"
						placeholder="e.g., First Bank of Nigeria"
						value={formData.bank_name}
						onChange={(e) =>
							setFormData({ ...formData, bank_name: e.target.value })
						}
						required
					/>

					<div className="flex items-center space-x-2">
						<Checkbox
							id="is_active_edit"
							checked={formData.is_active}
							onChange={(e) =>
								setFormData({ ...formData, is_active: e.target.checked })
							}
						/>
						<label
							htmlFor="is_active_edit"
							className="text-sm font-medium text-foreground cursor-pointer"
						>
							Active (visible to users)
						</label>
					</div>

					<Textarea
						label="Notes (Optional)"
						placeholder="Additional notes about this account..."
						value={formData.notes}
						onChange={(e) =>
							setFormData({ ...formData, notes: e.target.value })
						}
						rows={3}
					/>

					{/* Actions */}
					<div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
						<Button variant="outline" onClick={onClose} type="button">
							Cancel
						</Button>
						<Button type="submit" disabled={isLoading}>
							{isLoading ? "Updating..." : "Update Account"}
						</Button>
					</div>
				</form>
			</div>
		</div>
	);
};

export default EditPaymentAccountModal;
